// import * as React from 'react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { capitalCase } from 'change-case';
import { DefaultTooltip } from '../shared/DefaultTooltip';
import './EmailStatus.scss';

const toolTipMessage = 'Email must be sent to complete the save process';

export const EmailStatus = () => {
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const isEmailEdited = useSelector((state) => state.mail.isEmailEdited);
  const isEmailSaved = useSelector((state) => state.mail.isEmailSaved);
  const anyEmailSaved = Object.values(isEmailSaved).includes(true);
  const anyEmailNotSaved = Object.values(isEmailSaved).includes(false) || Object.keys(isEmailSaved).length === 0;
  const isPresaved = useSelector((state) => state.mail.presave);
  const saveEmailFailure = useSelector((state) => state.mail.error);
  const selectedRecords = useSelector((state) => state.mail.selectedRecords);
  const allFiles = useSelector((state) => state.files.docrioFiles);
  const numFilesSaving = allFiles.filter((file) => file.isSaving).length;
  const numFilesSaved = allFiles.filter((file) => file.saved).length;
  const numFilesFailed = allFiles.filter((file) => file.saveFailed).length;
  const numFilesNotSaved = allFiles.filter((file) => !file.saved).length;
  const isTimeEnabled = useSelector((state) => state.mail.settings?.timeSettings?.timeTrackingMode) !== 'timeDisabled';
  const timeTrackingRequired = useSelector((state) => state.mail.settings?.timeSettings?.timeTrackingMode) === 'timeRequired';
  const objectsConfiguredForTime = useSelector((state) => state.time?.config?.objectsConfiguredForTime);
  const timeObjectConfigured = Object.keys(selectedRecords).length ? !!Object.values(selectedRecords).find((record) => objectsConfiguredForTime?.find((obj) => obj === record.type)) : false;
  const timeRecords = useSelector((state) => state.time?.timeRecords);
  const timeRecordsSaved = timeRecords?.filter((time) => time.saved);
  const timeRecordsToSave = timeRecords?.filter((time) => !time.saved);
  const timeError = (useSelector((state) => state.time?.timeRecords) || []).filter((time) => time.errorMessage).length;
  const willSaveTime = timeRecordsToSave.length || (timeTrackingRequired && !timeRecordsSaved.length && timeObjectConfigured);
  const isProcessingTime = useSelector((state) => state.time.timeProcessing);

  const [statusText, setStatusText] = React.useState('');

  const emailStatusPreSaved = () => (
    <div className='statusWrapper'>
      <span className='statusText'> Email is pre-saved </span>
      {isComposingEmail && <div className='statusToolTip'><DefaultTooltip textContent={toolTipMessage} id={'emailStatusNotSavedTooltip'} /></div>}
    </div>
  );
  const emailStatusNotSaved = <span> Email is not saved yet</span>;
  const oneOrMany = (numFiles) => (numFiles > 1 ? 'Files are' : 'File is');

  // Step 1: Determine state
  const getEmailStatus = () => {
    const emailStatuses = [];

    if (isComposingEmail) {
      if ((anyEmailSaved || isPresaved) && !isEmailEdited) {
        emailStatuses.push('presaved');
      } else {
        emailStatuses.push('notSaved');
      }
      return emailStatuses;
    }
    if (saveEmailFailure && !anyEmailSaved) {
      emailStatuses.push('failed');
    }
    if (!saveEmailFailure && !anyEmailSaved && anyEmailNotSaved) {
      emailStatuses.push('notSaved');
    }
    if (!isComposingEmail && !saveEmailFailure && anyEmailSaved && anyEmailNotSaved) {
      emailStatuses.push('partiallySaved');
    } else if (anyEmailSaved) {
      emailStatuses.push('saved');
    }

    return emailStatuses;
  };

  const getFilesStatus = () => {
    // TODO: Need to handle the file statuses in a separate state like email (isEmailSaved)
    // Only a temp fix - if email is partially saved, then file/s is/are also partially saved
    const emailStatuses = getEmailStatus();
    if (emailStatuses.length === 1 && emailStatuses[0] === 'partiallySaved') {
      return ['partiallySaved'];
    }
    const fileStatuses = [];

    if (numFilesFailed) {
      fileStatuses.push('failed');
    }
    if (numFilesSaved) {
      fileStatuses.push('saved');
    }
    if (numFilesSaving) {
      fileStatuses.push('saving');
    }
    if (!numFilesFailed && !numFilesSaving && !numFilesSaved) {
      fileStatuses.push('notSaved');
    }

    return fileStatuses;
  };

  const getTimeStatus = () => {
    const timeStatuses = [];

    if (!isTimeEnabled) {
      return timeStatuses;
    }

    if (timeError) {
      timeStatuses.push('failed');
    }
    if (timeRecordsSaved.length) {
      timeStatuses.push('saved');
    }
    if (isProcessingTime) {
      timeStatuses.push('saving');
    }
    if (!timeError && willSaveTime && !isProcessingTime) {
      timeStatuses.push('notSaved');
    }

    return timeStatuses;
  };

  // Step 2: Group by state
  const groupByStatus = (statusMap) => {
    const failed = [];
    const saved = [];
    const saving = [];
    const notSaved = [];
    const partiallySaved = [];

    for (const key of Object.keys(statusMap)) {
      if (statusMap[key].includes('failed')) {
        failed.push(capitalCase(key));
      }
      if (statusMap[key].includes('saved')) {
        saved.push(capitalCase(key));
      }
      if (statusMap[key].includes('saving')) {
        saving.push(capitalCase(key));
      }
      if (statusMap[key].includes('notSaved')) {
        notSaved.push(capitalCase(key));
      }
      if (statusMap[key].includes('partiallySaved')) {
        partiallySaved.push(capitalCase(key));
      }
    }

    return { saved, notSaved, saving, failed, partiallySaved };
  };

  // Step 3: Create status string.
  const createStatusStrings = (groupedStatusMap) => {
    // Not Saved
    if (groupedStatusMap.notSaved.includes('Time') && groupedStatusMap.saved.includes('Time')) {
      groupedStatusMap.notSaved.splice(groupedStatusMap.notSaved.indexOf('Time'), 1, 'Some Time');
    }
    if (groupedStatusMap.notSaved.includes('Files') && groupedStatusMap.notSaved.length === 1) {
      groupedStatusMap.notSaved.splice(groupedStatusMap.notSaved.indexOf('Files'), 1, oneOrMany(numFilesNotSaved));
    }
    const containsEmail = Object.keys(groupedStatusMap).find((key) => groupedStatusMap[key].includes('Email'));

    let notSaved = '';
    switch (groupedStatusMap.notSaved.length) {
      case 0:
        notSaved = '';
        break;
      case 1:
        notSaved = <span>{groupedStatusMap.notSaved[0]}{groupedStatusMap.notSaved[0].includes('File') ? '' : ' is'} <b>not saved. </b></span>;
        break;
      case 2:
        notSaved = <span>{groupedStatusMap.notSaved[0]} &amp; {groupedStatusMap.notSaved[1]} are <b>not saved. </b></span>;
        break;
      case 3:
        notSaved = <span>{groupedStatusMap.notSaved[0]}, {groupedStatusMap.notSaved[1]}, &amp; {groupedStatusMap.notSaved[2]} are <b>not saved. </b></span>;
        break;
      default:
        notSaved = '';
        break;
    }

    // Saved
    let saved = '';
    if (groupedStatusMap.saved.includes('Files') && groupedStatusMap.saved.length === 1) {
      groupedStatusMap.saved.splice(groupedStatusMap.saved.indexOf('Files'), 1, oneOrMany(numFilesSaved));
    }
    switch (groupedStatusMap.saved.length) {
      case 0:
        saved = '';
        break;
      case 1:
        saved = <span>{groupedStatusMap.saved[0]}{groupedStatusMap.saved[0].includes('File') ? '' : ' is'} <b>saved. </b></span>;
        break;
      case 2:
        saved = <span>{groupedStatusMap.saved[0]} &amp; {groupedStatusMap.saved[1]} are <b>saved. </b></span>;
        break;
      case 3:
        saved = <span>{groupedStatusMap.saved[0]}, {groupedStatusMap.saved[1]}, &amp; {groupedStatusMap.saved[2]} are <b>saved. </b></span>;
        break;
      default:
        saved = '';
        break;
    }

    // Partially Saved
    let partiallySaved = '';
    if (groupedStatusMap.saved.includes('Files') && groupedStatusMap.saved.length === 1) {
      groupedStatusMap.saved.splice(groupedStatusMap.saved.indexOf('Files'), 1, oneOrMany(numFilesSaved));
    }
    switch (groupedStatusMap.partiallySaved.length) {
      case 0:
        partiallySaved = '';
        break;
      case 1:
        partiallySaved = <span>{groupedStatusMap.partiallySaved[0]}{groupedStatusMap.partiallySaved[0].includes('File') ? '' : ' is'} <b>partially saved. </b></span>;
        break;
      case 2:
        partiallySaved = <span>{groupedStatusMap.partiallySaved[0]} &amp; {groupedStatusMap.partiallySaved[1]} are <b>partially saved. </b></span>;
        break;
      case 3:
        partiallySaved = <span>{groupedStatusMap.partiallySaved[0]}, {groupedStatusMap.partiallySaved[1]}, &amp; {groupedStatusMap.partiallySaved[2]} are <b>partially saved. </b></span>;
        break;
      default:
        partiallySaved = '';
        break;
    }

    // Saving
    let saving = '';
    if (groupedStatusMap.saving.includes('Files') && groupedStatusMap.saving.length === 1) {
      groupedStatusMap.saving.splice(groupedStatusMap.saving.indexOf('Files'), 1, oneOrMany(numFilesSaving));
    }
    switch (groupedStatusMap.saving.length) {
      case 0:
        saving = '';
        break;
      case 1:
        saving = <span>{groupedStatusMap.saving[0]} <b>saving... </b></span>;
        break;
      case 2:
        saving = <span>{groupedStatusMap.saving[0]} &amp; {groupedStatusMap.saving[1]} are <b>saving... </b></span>;
        break;
      default:
        saving = '';
        break;
    }

    // Failed
    let failed = '';
    if (groupedStatusMap.failed.includes('Time') && groupedStatusMap.saved.includes('Time')) {
      groupedStatusMap.failed.splice(groupedStatusMap.notSaved.indexOf('Time'), 1, 'Some Time');
    }
    switch (groupedStatusMap.failed.length) {
      case 0:
        failed = '';
        break;
      case 1:
        failed = <span>{groupedStatusMap.failed[0]} <span style={{ color: 'red' }}>failed</span> to save. </span>;
        break;
      case 2:
        failed = <span>{groupedStatusMap.failed[0]} &amp; {groupedStatusMap.failed[1]} <span style={{ color: 'red' }}>failed</span> to save. </span>;
        break;
      case 3:
        failed = <span>{groupedStatusMap.failed[0]}, {groupedStatusMap.failed[1]}, &amp; {groupedStatusMap.failed[2]} <span style={{ color: 'red' }}>failed</span> to save. </span>;
        break;
      default:
        failed = '';
        break;
    }

    return {
      containsEmail,
      saved,
      notSaved,
      saving,
      failed,
      partiallySaved,
    };
  };

  // Step 4: Order the strings
  const orderStatusStrings = (statusStrings) => {
    const order = [];
    const { containsEmail } = statusStrings;

    order.push(containsEmail);
    for (const key of Object.keys(statusStrings)) {
      if (key !== containsEmail && key !== 'containsEmail') {
        order.push(key);
      }
    }
    return <span>{statusStrings[order[0]]}{statusStrings[order[1]]}{statusStrings[order[2]]}{statusStrings[order[3]]}</span>;
  };

  // Step 5: Call functions. Use useEffect to make sure it updates based on state changes
  const getCurrentStatus = () => {
    const statusMap = {
      email: [],
      files: [],
      time: [],
    };

    statusMap.email = getEmailStatus();
    if (statusMap.email.includes('presaved')) {
      return emailStatusPreSaved();
    } else if (isComposingEmail && statusMap.email.includes('notSaved')) {
      return emailStatusNotSaved;
    }

    statusMap.files = getFilesStatus();
    statusMap.time = getTimeStatus();

    const groupedStatusMap = groupByStatus(statusMap);
    const statusStrings = createStatusStrings(groupedStatusMap);
    const orderedStatusString = orderStatusStrings(statusStrings);
    return orderedStatusString;
  };

  useEffect(() => {
    const status = getCurrentStatus();
    setStatusText(status);
  }, [isComposingEmail, saveEmailFailure, isEmailSaved,
    isPresaved, numFilesFailed, numFilesSaved, numFilesSaving,
    timeError, timeRecords, isProcessingTime, willSaveTime]);

  return (
    <span className='email-status'>
      <div id='email-status'>
        { statusText }
      </div>
    </span>
  );
};
